import { Injectable, inject } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { CalculatedOdds, Fraction, OddsFormatterService, OddsOperations } from '@frontend/sports/odds/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';

@Injectable()
export class SummaryCalculatorService {
    private readonly oddsFormatSettings = inject(UserOddsFormatSettingsService);
    private readonly oddsFormatter = inject(OddsFormatterService);
    private readonly betslipConfig = inject(BetslipConfig);

    calculateWinnings(prices: NativePrice[], stake: number): number | undefined {
        if (!prices.length) {
            return;
        }

        const totalOdds = this.getTotalOddsValue(prices);
        return this.calculateOdds(totalOdds, stake);
    }

    calculateWinningsForGroup(stake: number, precreatedOptionsOdds: NativePrice): number {
        const totalOdds = this.convertToCalculatedOdds(precreatedOptionsOdds);
        return this.calculateOdds(totalOdds, stake);
    }

    getTotalOdds(prices: NativePrice[]): string {
        if (!prices.length) {
            return '-';
        }

        const totalOdds = this.getTotalOddsValue(prices);
        return this.formatOdds(totalOdds);
    }

    getTotalOddsForGroup(precreatedOptionsOdds: NativePrice): string {
        return this.formatOdds(this.convertToCalculatedOdds(precreatedOptionsOdds));
    }

    private getTotalOddsValue(prices: NativePrice[]): CalculatedOdds {
        const convertedPrices = prices.map((price) => this.convertToCalculatedOdds(price));
        return OddsOperations.multiply(convertedPrices, this.betslipConfig.roundPriceValues);
    }

    private calculateOdds(odds: CalculatedOdds, stake: number): number {
        const decimalOdds = OddsOperations.toDecimalValue(odds, this.oddsFormatSettings.oddsFormat);
        return decimalOdds.times(stake).toNumber();
    }

    private convertToCalculatedOdds(price: NativePrice): CalculatedOdds {
        return {
            decimals: price.odds,
            fractional: new Fraction(price.numerator, price.denominator),
            moneyline: price.americanOdds,
        };
    }

    private formatOdds(odds: CalculatedOdds): string {
        return this.oddsFormatter.format(odds, {
            displayFormat: this.oddsFormatSettings.oddsFormat,
            isSummary: true,
        });
    }
}
